import {Alert, Button, Col, List, message, Modal, Popover, Row, Space, Typography} from "antd";
import {CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, DownOutlined, PaperClipOutlined} from "@ant-design/icons";
import {useEffect, useMemo, useState} from "react";
import {MinimumDocumentModel, NegotiationAnalysisModel} from "../../types/negotiations";
import {useContinueAnalysisMinimumDataMutation, useGetNegotiationDetailQuery, useUpdateNegotiationAnalysisMutation} from "../../redux/api/negotiationsApiSlice";
import {skipToken} from "@reduxjs/toolkit/query";
import {useParams} from "react-router-dom";
import {JOB_STATUS} from "../../types/reconciliation";
import UploadMinimumDoc from "./UploadMinimumDoc";
import {useLazyGetDocumentUrlQuery} from "../../redux/api/documentsApiSlice";
import useFileDownloader from "../../hooks/useFileDownloader";
import {
  assistantBg,
  assistantBorder,
  assistantColor,
  CustomRobotIcon
} from "./sidebars/AIAssistant/utils/CustomRobotIcon";
import ReactMarkdown from "react-markdown";

export default function MinimumInfo({isCurrentStep, startPolling, callBackUpdateNegotiation}: {
    isCurrentStep: boolean,
    startPolling: () => void,
    callBackUpdateNegotiation: () => void
}) {
    const {id} = useParams()
    const {downloadFile} = useFileDownloader()

    const {data: negotiationData} = useGetNegotiationDetailQuery(id ?? skipToken)
    const [getUrl] = useLazyGetDocumentUrlQuery()
    const [updateNegAnalysis] = useUpdateNegotiationAnalysisMutation()
    const [triggerContinueAnalysis, {isLoading: continueAnalysisLoading}] = useContinueAnalysisMinimumDataMutation()

    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<"docs" | "fields" | null>(null);
    const [analysisData, setAnalysisData] = useState<NegotiationAnalysisModel | undefined>(undefined)
    const [popoverContinueOpen, setPopoverContinueOpen] = useState<boolean>(false)
    const [documentToDelete, setDocumentToDelete] = useState<{docId: string, docName: string, mediaLabelUuid: string} | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

    const missingDocRequired = useMemo(
        () => !!analysisData?.minimum_documents && analysisData.minimum_documents.length > 0,
        [analysisData?.minimum_documents]
    );
    const missingInfoRequired = useMemo(
        () => !!analysisData?.minimum_info && analysisData?.minimum_info.length > 0,
        [analysisData?.minimum_info]
    );

    const hasMissingData = useMemo(() => {
        if (!analysisData) return false;

        let hasMissingDoc = false;
        let hasMissingInfo = false;

        if (analysisData.minimum_documents) {
            const minDocArray = Array.isArray(analysisData.minimum_documents) ? analysisData.minimum_documents : [analysisData.minimum_documents];

            hasMissingDoc = minDocArray.some(doc => doc.documents.length === 0);
        }

        if (analysisData.minimum_info) {
            const minInfoArray = Array.isArray(analysisData.minimum_info) ? analysisData.minimum_info : [analysisData.minimum_info];

            hasMissingInfo = minInfoArray.some(info => info.value === null);
        }

        return hasMissingDoc || hasMissingInfo;
    }, [analysisData]);

    useEffect(() => {
        if (negotiationData) {
            setAnalysisData(negotiationData.negotiation_analysis)
        }
    }, [negotiationData]);

    const continueAnalysis = () => {
        if (id && analysisData) {
            triggerContinueAnalysis(
                {
                    negID: id,
                    minimum_documents: analysisData?.minimum_documents,
                    minimum_info: analysisData?.minimum_info
                })
                .unwrap()
                .then(res => startPolling())
        }
    }

    const formatInfo = (item: {key: string, value: string | number | boolean | null, label: string} ) => {
        let formattedValue = item.value
        return <strong>{formattedValue}</strong>
    }

    const renderInfoList = (
        items: {key: string, value: string | number | boolean | null, label: string}[],
        shortList: boolean = true
    ) => (
        <List
            dataSource={shortList ? items.slice(0, 5) : items}
            size="small"
            renderItem={(item, i) => {
                const isAvailable = item.value !== null;
                const value = isAvailable ? item.value : ""
                return (
                    <List.Item key={i}>
                    <div style={{width: "100%"}}>
                        <Row justify={"space-between"}>
                            <Typography.Text>{item.key}</Typography.Text>
                            {isAvailable ? (
                                <CheckCircleOutlined style={{color: "#52c41a"}}/>
                            ) : (
                                <CloseCircleOutlined style={{color: "#ff4d4f"}}/>
                            )}
                        </Row>
                        <div style={{marginTop: 8}}>
                            {formatInfo(item)}
                        </div>
                    </div>
                </List.Item>
                );
            }}
        />
    );

    const handleOpenDocument = (docId: string) => {
        getUrl({negotiation_uuid: id, uuid: docId}).unwrap()
            .then((response) => {
                    downloadFile(response.url)
                }
            )
            .catch((e: any) => {
                message.error("Impossibile aprire il file")
                console.error("Open file", e)
            })
    }


    const handleDeleteDocument = () => {
        const updatedData = analysisData?.minimum_documents.map(item => {
            if (item.media_label_uuid === documentToDelete?.mediaLabelUuid) {
                const filteredDocuments = item.documents.filter(doc =>
                    doc.document_uuid !== documentToDelete?.docId
                );

                return {
                    ...item,
                    documents: filteredDocuments
                };
            }
            return item;
        });

        if (id) {
            updateNegAnalysis({negID: id, minimum_documents: updatedData}).unwrap()
                .then(res => callBackUpdateNegotiation())
        }

        setDeleteModalOpen(false)
        setDocumentToDelete(null)
    }

    const renderSingleDocument = (doc: {
        document_name: string;
        document_uuid: string;
        document_s3_path: string;
        media_uuid: string;
        media_s3_path: string;
    }, mediaLabelUuid: string) => {
        return <Row justify={"space-between"}>
            <Col span={21} style={{overflow: "hidden"}}>
                <Button
                    type={"link"}
                    icon={<PaperClipOutlined/>}
                    style={{padding: 0}}
                    onClick={() => handleOpenDocument(doc.document_uuid)}>
                    {doc.document_name}
                </Button>
            </Col>
            {isCurrentStep && <Col span={3}>
                <Button
                    danger
                    type={"link"}
                    icon={<DeleteOutlined/>}
                    onClick={() => {
                        setDocumentToDelete({docId: doc.document_uuid, docName: doc.document_name, mediaLabelUuid})
                        setDeleteModalOpen(true)
                    }}
                />
            </Col>}
        </Row>
    }

    const renderDocumentList = (items: MinimumDocumentModel, shortList: boolean = true) => {
        return <div style={{maxHeight: '300px', overflowY: 'auto'}}><List
            dataSource={shortList ? items.slice(0, 5) : items}
            size="small"
            renderItem={(item, i) => {
                const isEmpty = item.documents.length === 0
                return (
                    <div key={i} style={{marginTop: 10, borderBottom: "1px solid #DCDCDC"}}>
                        <Row justify={"space-between"}>
                            <Typography.Title level={5} style={{marginTop: 16}}>{item.media_label_name}</Typography.Title>
                            {!isEmpty ? (
                                <CheckCircleOutlined style={{color: "#52c41a"}}/>
                            ) : (
                                <CloseCircleOutlined style={{color: "#ff4d4f"}}/>
                            )}
                        </Row>
                        <div style={{marginTop: 5}}>
                            {item.documents.length > 0 ? item.documents.map(doc => <div>{renderSingleDocument(doc, item.media_label_uuid)}</div>) : null}
                        </div>
                    </div>
                );
            }}
        />
        </div>
    }

    const showModal = (type: "docs" | "fields") => {
        setModalType(type);
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setModalType(null);
    };

    const continueAnalysisPopoverContent = () => {
        return <div>
            Continuando con l'operazione i dati attualmente presenti potranno essere sovrascritti. <br/>
            Si desidera continuare?
            <Row justify={"end"} style={{marginTop: "1rem"}}>
                <Button size={"small"} type={"primary"} onClick={() => continueAnalysis()}>
                    Continua analisi
                </Button>
            </Row>
        </div>
    }

    return (
        <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
            <Space direction="vertical" style={{display: "flex"}}>
                { analysisData?.minimum_info_suggestion_message && (
                    <Row>
                        <Col xs={24}>
                            <Alert
                                style={{color: assistantColor, backgroundColor: assistantBg, borderColor: assistantBorder}}
                                type={"info"}
                                message={<strong style={{color: assistantColor}}>ERIK:</strong>}
                                description={<ReactMarkdown>{ analysisData?.minimum_info_suggestion_message }</ReactMarkdown>}
                                showIcon
                                icon={<CustomRobotIcon/>}
                            />
                        </Col>
                    </Row>
                )}
                <Row gutter={[16, 16]} style={{marginTop: "1rem"}}>
                    { missingDocRequired && (
                        <Col xs={24} sm={missingInfoRequired ? 12 : 24}>
                            <Typography.Title level={5}>Documenti minimi</Typography.Title>
                            <div
                                style={{
                                    padding: 10,
                                    border: "1px solid #DCDCDC",
                                    borderRadius: 5,
                                }}
                            >
                                {renderDocumentList(analysisData?.minimum_documents as MinimumDocumentModel, false)}
                                {isCurrentStep && analysisData?.minimum_documents && <Row justify="end" style={{marginTop: "1rem"}}>
                                    <UploadMinimumDoc
                                        minimumDocuments={analysisData.minimum_documents}
                                        documentTypeList={analysisData.minimum_documents.map(el => ({label: el.media_label_name, value: el.media_label_uuid}))}
                                        callBackUpdateNegotiation={() => callBackUpdateNegotiation()}
                                    />
                                </Row>}
                            </div>
                        </Col>
                    )}
                    { missingInfoRequired && (
                        <Col xs={24} sm={missingDocRequired ? 12 : 24}>
                            <Typography.Title level={5} style={{marginTop: 16}}>
                                Dati necessari
                            </Typography.Title>
                            <div
                                style={{
                                    padding: 10,
                                    border: "1px solid #DCDCDC",
                                    borderRadius: 5,
                                }}
                            >
                                {renderInfoList(analysisData?.minimum_info as {key: string, value: string | number | boolean | null, label: string}[])}
                                <Row justify="end">
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => showModal("fields")}
                                    >
                                        Vedi tutto
                                    </Button>
                                </Row>
                            </div>
                        </Col>
                    )}
                    {isCurrentStep && hasMissingData && <Col span={24}>
                        <Alert
                            type="warning"
                            showIcon
                            message={"NB. Inserire i dati mancanti prima di proseguire l'analisi."}/>
                    </Col>}
                    { !missingDocRequired && !missingInfoRequired && (
                        <Col xs={12}>
                            <Typography.Paragraph>Nessun documento minimo o dato necessario richiesto.</Typography.Paragraph>
                        </Col>
                    )}
                    {analysisData?.analysis_status !== JOB_STATUS.ERROR && isCurrentStep && <Col span={24}>
                        <Row justify={"center"}>
                            <Popover placement={"top"}
                                     title={"Attenzione"}
                                     content={continueAnalysisPopoverContent()}
                                     open={popoverContinueOpen}
                                     trigger={"click"}
                                     onOpenChange={open => {
                                       if (!hasMissingData) {
                                         setPopoverContinueOpen(open);
                                       }
                                     }}>
                                <Button
                                    type={"primary"}
                                    icon={<DownOutlined/>}
                                    disabled={ hasMissingData }
                                >
                                    Continua analisi
                                </Button>
                            </Popover>
                        </Row>
                    </Col>}
                </Row>
            </Space>
            <Modal
                title={"Rimuovi documento"}
                open={deleteModalOpen}
                okButtonProps={{danger: true}}
                okText={"Rimuovi"}
                onOk={()=> handleDeleteDocument()}
                cancelButtonProps={{style: {display: "none"}}}
                onCancel={()=> {
                    setDeleteModalOpen(false)
                    setDocumentToDelete(null)
                }}
            >
                Vuoi rimuovere il seguente documento dai documenti minimi? <br/>
                {documentToDelete?.docName}
            </Modal>
            <Modal
                title={"Dati necessari"}
                open={modalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                {modalType === "fields" && (
                    <div style={{padding: 10}}>
                        {analysisData && renderInfoList(analysisData.minimum_info, false)}
                    </div>
                )}
            </Modal>
        </div>
    );
}
